import { MotifButton } from '@ey-xd/motif-wc-react';
import { useQueryClient } from '@tanstack/react-query';
import { authModule } from '@/auth/auth-module';
import { useClientStore } from '@/client/client-store';
import { getTheme, setTheme } from '@/motif/setup';
import { eventsModule } from '@/events/events-module';
import { getTracker } from '@/telemetry/tracker';

function readStorage(key: string): string {
  try {
    return sessionStorage.getItem(key) ?? localStorage.getItem(key) ?? 'Not set';
  } catch {
    return 'Unavailable';
  }
}

export function SettingsPage() {
  const queryClient = useQueryClient();
  const currentClient = useClientStore((s) => s.current);
  const availableClients = useClientStore((s) => s.available);
  const theme = getTheme();
  const nextTheme = theme === 'light' ? 'dark' : 'light';

  const refreshClients = () => {
    getTracker().event('Settings-refresh-clients');
    eventsModule.emit('shell:refresh-clients', { force: true });
    void queryClient.invalidateQueries({ queryKey: ['clients'] });
  };

  return (
    <section className="settings-page" aria-labelledby="settings-page-title">
      <div className="settings-page__header">
        <h1 id="settings-page-title">Settings</h1>
        <p>Shell preferences and current session context.</p>
      </div>

      <div className="settings-page__grid">
        <section className="settings-page__panel" aria-labelledby="settings-appearance-title">
          <h2 id="settings-appearance-title">Appearance</h2>
          <dl>
            <div>
              <dt>Theme</dt>
              <dd>{theme}</dd>
            </div>
          </dl>
          <MotifButton
            variant="secondary"
            onClick={() => {
              getTracker().event('Settings-theme-toggle', { theme: nextTheme });
              setTheme(nextTheme);
            }}
          >
            Switch to {nextTheme}
          </MotifButton>
        </section>

        <section className="settings-page__panel" aria-labelledby="settings-client-title">
          <h2 id="settings-client-title">Client</h2>
          <dl>
            <div>
              <dt>Selected client</dt>
              <dd>{currentClient?.name ?? 'Not selected'}</dd>
            </div>
            <div>
              <dt>Client id</dt>
              <dd>{currentClient?.id ?? 'Not set'}</dd>
            </div>
            <div>
              <dt>Available clients</dt>
              <dd>{availableClients.length}</dd>
            </div>
          </dl>
          <MotifButton variant="secondary" onClick={refreshClients}>
            Refresh clients
          </MotifButton>
        </section>

        <section className="settings-page__panel" aria-labelledby="settings-user-title">
          <h2 id="settings-user-title">User</h2>
          <dl>
            <div>
              <dt>Name</dt>
              <dd>{authModule.user?.name ?? 'Not signed in'}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{authModule.user?.email ?? 'Not available'}</dd>
            </div>
          </dl>
          <MotifButton variant="secondary" onClick={() => authModule.logout()}>
            Sign out
          </MotifButton>
        </section>

        <section className="settings-page__panel" aria-labelledby="settings-legacy-title">
          <h2 id="settings-legacy-title">Legacy context</h2>
          <dl>
            <div>
              <dt>currentlySelectedClientId</dt>
              <dd>{readStorage('currentlySelectedClientId')}</dd>
            </div>
            <div>
              <dt>Environment</dt>
              <dd>{import.meta.env.VITE_ENV}</dd>
            </div>
          </dl>
        </section>
      </div>
    </section>
  );
}
